import React from 'react';
import PropTypes from 'prop-types';
import {
  Activity,
  Target,
  Sparkles,
  Check,
  X,
  AlertTriangle,
} from 'lucide-react';
import { cn } from 'src/lib/utils';
import ScoreRing from 'src/components/common/ScoreRing';
import { TIERS, tierColorVar, tintFrom } from '../../SubmitCandidate/matchData';

// senior_level_flow's New/Closed tabs — accordion content for a candidate
// row, built off the candidate-list API's `match_details` object (see
// SeniorNewTab.jsx/SeniorFailedTab.jsx for where `expandable: true` turns
// this on). Deliberately a lighter read than SubmitCandidate/
// CandidateMatchRow.jsx (this session's own design reference): no
// timeline/insight/select-to-submit actions here — those are about
// evaluating NEW matches before submitting, not reviewing a candidate
// already in this pipeline. Just the score itself: breakdown, skill
// coverage/fit, and the backend's own one-line summary.

// "NEAR_MATCH" → 'near' (matches matchData.js's TIERS keys). Falls back to
// a score-threshold bucket for any match_type this doesn't recognize, so an
// unexpected enum value still renders a sensible color instead of the
// 'explore' default swallowing a genuinely strong match.
const tierFromMatchType = (matchType) => {
  const key = (matchType || '').toLowerCase().replace('_match', '');
  return TIERS[key] ? key : null;
};
const tierFromScore = (score) => {
  if (score >= TIERS.best.min) return 'best';
  if (score >= TIERS.near.min) return 'near';
  return 'explore';
};

const FIT_STATE_CFG = {
  pass: {
    color: 'var(--ds-success)',
    cls: 'text-muted-foreground',
    Icon: Check,
  },
  warn: {
    color: 'var(--ds-warning)',
    cls: 'text-ds-warning',
    Icon: AlertTriangle,
  },
  fail: { color: 'var(--ds-danger)', cls: 'text-ds-danger', Icon: X },
};
const fitState = (passed) => {
  if (passed === true) return 'pass';
  if (passed === false) return 'fail';
  return 'warn';
};
const FitItem = ({ passed, label }) => {
  const { color, cls, Icon } = FIT_STATE_CFG[fitState(passed)];
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[11.5px]', cls)}>
      <Icon style={{ width: 11, height: 11, strokeWidth: 2.2, color }} />
      {label}
    </span>
  );
};
FitItem.propTypes = {
  passed: PropTypes.bool,
  label: PropTypes.string.isRequired,
};
FitItem.defaultProps = { passed: null };

const ScoreBar = ({ label, value, color }) => {
  const pct = Math.round(value ?? 0);
  return (
    <div className="grid grid-cols-[120px_1fr_34px] items-center gap-2.5">
      <span className="text-[11.5px] font-medium truncate text-muted-foreground">
        {label}
      </span>
      <span className="h-2 rounded overflow-hidden bg-border">
        <span
          className="block h-full rounded"
          style={{
            width: `${pct}%`,
            background: color,
            transition: 'width .5s cubic-bezier(.4,0,.2,1)',
          }}
        />
      </span>
      <span className="font-mono text-[12px] font-bold tabular-nums text-right text-foreground-strong">
        {pct}
      </span>
    </div>
  );
};
ScoreBar.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  color: PropTypes.string.isRequired,
};
ScoreBar.defaultProps = { value: null };

const FIT_MAP_STATUS_CLS = {
  matched:
    'bg-green-100 dark:bg-green-950/50 text-green-800 dark:text-green-400 border-green-200 dark:border-green-800',
  partial:
    'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  missing: 'bg-muted text-muted-foreground border-border',
};
const FIT_MAP_STATUS_ICON = { matched: Check, missing: X };
const FitMapChip = ({ name, status, required }) => {
  const Icon = FIT_MAP_STATUS_ICON[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-[5px] text-[11.5px] font-[550] border',
        FIT_MAP_STATUS_CLS[status] || FIT_MAP_STATUS_CLS.missing
      )}
    >
      {Icon && <Icon style={{ width: 10, height: 10, strokeWidth: 2.5 }} />}
      {name}
      {required && (
        <span className="text-[8.5px] font-bold tracking-[0.04em] opacity-75 ml-0.5">
          REQ
        </span>
      )}
    </span>
  );
};
FitMapChip.propTypes = {
  name: PropTypes.string.isRequired,
  status: PropTypes.string,
  required: PropTypes.bool,
};
FitMapChip.defaultProps = { status: 'missing', required: false };

const Panel = ({ children }) => (
  <div className="flex flex-col rounded-[10px] border border-border bg-card p-[16px_18px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
    {children}
  </div>
);
Panel.propTypes = { children: PropTypes.node.isRequired };

const SectionLabel = ({ icon: Icon, children, meta, color }) => (
  <div className="flex items-center justify-between mb-3">
    <span className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
      <Icon style={{ width: 12, height: 12, strokeWidth: 2 }} />
      {children}
    </span>
    {meta != null && (
      <span
        className="font-mono text-[10.5px] font-semibold"
        style={{ color: color || 'var(--foreground-strong)' }}
      >
        {meta}
      </span>
    )}
  </div>
);
SectionLabel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  meta: PropTypes.node,
  color: PropTypes.string,
};
SectionLabel.defaultProps = { meta: null, color: undefined };

const MatchDetailsPanel = ({ matchDetails }) => {
  if (!matchDetails) {
    return (
      <div className="px-6 py-8 text-center text-[12.5px] text-muted-foreground">
        Match score details aren&apos;t available for this candidate yet.
      </div>
    );
  }

  const {
    match_score: matchScore,
    match_type: matchType,
    confidence,
    score_summary: scoreSummary,
    fit_map: fitMap = [],
    skill_match_details: skillDetails = {},
  } = matchDetails;
  const {
    coverage = {},
    fit_flags: fitFlags = [],
    must_have: mustHave,
    nice_to_have: niceToHave,
    score_breakdown: scoreBreakdown = [],
    total_experience: totalExperience,
    relevant_experience: relevantExperience,
  } = skillDetails;

  const tier = tierFromMatchType(matchType) || tierFromScore(matchScore);
  const accent = tierColorVar(tier);
  const score = Math.round(matchScore || 0);
  const coverageTotal =
    coverage.required_total ||
    (coverage.matched || 0) +
      (coverage.partial || 0) +
      (coverage.missing || 0) ||
    1;

  return (
    <div
      className="px-6 py-5"
      style={{
        borderLeft: `4px solid ${accent}`,
        borderTop: '1px solid var(--border)',
      }}
    >
      {scoreSummary && (
        <div
          className="flex gap-3.5 items-center rounded-[10px] bg-card p-[14px_18px] mb-5 shadow-[0_1px_2px_rgba(0,0,0,0.03)] border"
          style={{ borderColor: tintFrom(accent, 22) }}
        >
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: tintFrom(accent, 18), color: accent }}
          >
            <Sparkles style={{ width: 16, height: 16, strokeWidth: 1.8 }} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.06em] mb-1">
              <span style={{ color: accent }}>AI Match Analysis</span>
              <span
                className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.06em]"
                style={{ background: accent, color: '#fff' }}
              >
                {TIERS[tier]?.label || tier}
              </span>
              {confidence != null && (
                <span className="font-mono text-[10px] font-semibold normal-case text-muted-foreground">
                  {Math.round(confidence * 100)}% confidence
                </span>
              )}
            </div>
            <p className="text-[12.5px] text-foreground-strong leading-[1.6] m-0">
              {scoreSummary}
            </p>
          </div>
          <ScoreRing score={score} color={accent} size={48} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {scoreBreakdown.length > 0 && (
          <Panel>
            <SectionLabel
              icon={Activity}
              meta={`${score} / 100`}
              color={accent}
            >
              Score Breakdown
            </SectionLabel>
            <div className="flex flex-col gap-[11px]">
              {scoreBreakdown
                .filter((s) => s.available)
                .map((s) => (
                  <ScoreBar
                    key={s.key}
                    label={s.label}
                    value={s.value}
                    color={accent}
                  />
                ))}
            </div>
            {(mustHave ||
              niceToHave ||
              totalExperience ||
              relevantExperience) && (
              <>
                <div className="h-px my-3.5 bg-border" />
                <div className="flex flex-col gap-2">
                  {mustHave && (
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="font-medium text-muted-foreground">
                        Must-have skills
                      </span>
                      <span className="font-semibold tabular-nums text-foreground-strong">
                        {mustHave.matched}/{mustHave.total}
                        {mustHave.partial
                          ? ` (+${mustHave.partial} partial)`
                          : ''}
                      </span>
                    </div>
                  )}
                  {niceToHave && (
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="font-medium text-muted-foreground">
                        Nice-to-have skills
                      </span>
                      <span className="font-semibold tabular-nums text-foreground-strong">
                        {niceToHave.matched}/{niceToHave.total}
                      </span>
                    </div>
                  )}
                  {totalExperience && (
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="font-medium text-muted-foreground">
                        Total experience
                      </span>
                      <span className="font-semibold text-foreground-strong">
                        {totalExperience}
                      </span>
                    </div>
                  )}
                  {relevantExperience && (
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="font-medium text-muted-foreground">
                        Relevant experience
                      </span>
                      <span className="font-semibold text-foreground-strong">
                        {relevantExperience}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </Panel>
        )}

        <Panel>
          <SectionLabel
            icon={Target}
            meta={`${coverage.matched ?? 0} / ${coverageTotal}`}
            color={accent}
          >
            Skill Fit
          </SectionLabel>

          <div className="mb-3.5">
            <div className="h-[9px] rounded-[5px] overflow-hidden flex bg-border border border-black/[0.03] dark:border-transparent">
              <span
                style={{
                  width: `${((coverage.matched || 0) / coverageTotal) * 100}%`,
                  background: 'var(--ds-success)',
                  transition: 'width .5s',
                }}
              />
              <span
                style={{
                  width: `${((coverage.partial || 0) / coverageTotal) * 100}%`,
                  background: 'var(--ds-warning)',
                  transition: 'width .5s',
                }}
              />
            </div>
            <div className="flex gap-3.5 text-[10.5px] mt-[7px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <span
                  className="w-2 h-2 rounded-[2px] inline-block"
                  style={{ background: 'var(--ds-success)' }}
                />
                Matched
              </span>
              {coverage.partial > 0 && (
                <span className="inline-flex items-center gap-1">
                  <span
                    className="w-2 h-2 rounded-[2px] inline-block"
                    style={{ background: 'var(--ds-warning)' }}
                  />
                  Partial
                </span>
              )}
              {coverage.missing > 0 && (
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-[2px] inline-block bg-border border border-border" />
                  Gaps
                </span>
              )}
            </div>
          </div>

          {fitMap.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 mb-3.5">
              {fitMap.map((f) => (
                <FitMapChip
                  key={f.name}
                  name={f.name}
                  status={f.status}
                  required={f.required}
                />
              ))}
            </div>
          ) : (
            <span className="text-[11.5px] text-muted-foreground mb-3.5 block">
              No JD skills defined.
            </span>
          )}

          {fitFlags.length > 0 && (
            <>
              <div className="h-px mb-3 bg-border" />
              <div className="flex flex-col gap-1.5">
                {fitFlags.map((f) => (
                  <FitItem key={f.field} passed={f.passed} label={f.label} />
                ))}
              </div>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
};

MatchDetailsPanel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  matchDetails: PropTypes.object,
};
MatchDetailsPanel.defaultProps = { matchDetails: null };

export default MatchDetailsPanel;
